import { CustomChart, HeatmapChart as HeatmapChartComponent, ScatterChart } from 'echarts/charts';
import { TooltipComponent, VisualMapComponent, GridComponent, GraphicComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedValue } from '../../../shared/hooks/use-debounced-value';
import { useElementSize } from '../../../shared/hooks/useElementSize';
import type { HeatmapChartZAxisParams } from '../../store/ChartStore/HeatmapChartStore';
import type { HeatmapBinIndex, HeatmapDataPoint } from '../../store/ChartStore/types';
import type { HeatmapWidgetAxisParams } from '../../store/types';
import type { HeatmapCorridorOverlay, HeatmapWidgetAppParams } from '../../types';

import { HEATMAP_THEME_NAMES, registerHeatmapThemes } from './heatmapThemeConfig';
import { useHeatmapChartOptions } from './hooks/useHeatmapChartOptions';

echarts.use([
  HeatmapChartComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
  GridComponent,
  GraphicComponent,
  ScatterChart,
  CustomChart,
]);

registerHeatmapThemes();

export type HeatmapSharedHoverState = {
  dataIndex: number | null;
  sourceId: string | null;
};

type HeatmapEventParams = echarts.ECElementEvent & {
  seriesType?: string;
  dataIndex?: number;
};

type HeatmapChartProps = {
  data: HeatmapDataPoint[];
  activeBins: HeatmapBinIndex[];
  xAxisValues: number[];
  yAxisValues: number[];
  zAxisParams: HeatmapChartZAxisParams;
  xAxisParams: HeatmapWidgetAxisParams;
  yAxisParams: HeatmapWidgetAxisParams;
  xAxisLabelKey: string;
  yAxisLabelKey: string;
  zAxisLabelKey: string;
  isAltNames: boolean;
  themeKey: HeatmapWidgetAppParams['theme'];
  chartId: string;
  sharedHover?: HeatmapSharedHoverState;
  onSharedHoverChange?: (dataIndex: number | null, sourceId: string) => void;
  valueField: 'rop' | 'density' | 'mse';
  suppressTooltips?: boolean;
  corridorOverlay?: HeatmapCorridorOverlay | null;
};

export const HeatmapChart = observer(
  ({
    themeKey,
    data,
    activeBins,
    xAxisValues,
    yAxisValues,
    zAxisParams,
    xAxisParams,
    yAxisParams,
    xAxisLabelKey,
    yAxisLabelKey,
    zAxisLabelKey,
    isAltNames,
    chartId,
    sharedHover,
    onSharedHoverChange,
    valueField,
    suppressTooltips,
    corridorOverlay,
  }: HeatmapChartProps) => {
    const { t } = useTranslation('heatmapWidget');

    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.EChartsType | null>(null);
    const isChartReadyRef = useRef(false);
    const size = useElementSize(chartRef);
    const { width = 1, height = 1 } = useDebouncedValue(size, 120);

    const themeName = HEATMAP_THEME_NAMES[themeKey];
    const xAxisName = isAltNames ? xAxisParams.name : t(xAxisLabelKey);
    const yAxisName = isAltNames ? yAxisParams.name : t(yAxisLabelKey);

    let zAxisNameTranslated = t(zAxisLabelKey);
    if (isAltNames) {
      zAxisNameTranslated = zAxisParams.name;
    }
    if (valueField === 'density') {
      zAxisNameTranslated = t('Heatmap-visibility-density');
    }

    const options = useHeatmapChartOptions({
      data,
      activeBins,
      xAxisValues,
      yAxisValues,
      zAxisParams,
      yAxisParams,
      xAxisParams,
      xAxisName,
      yAxisName,
      containerHeight: height,
      themeKey,
      valueField,
      zAxisNameTranslated,
      suppressTooltips,
      corridorOverlay,
    });

    const runSafelyOnChart = useCallback(<T,>(action: (chart: echarts.EChartsType) => T): T | undefined => {
      const chart = chartInstanceRef.current;
      const container = chartRef.current;
      const isDisposed = chart?.isDisposed?.();
      if (!chart || isDisposed || !container || !container.isConnected || !isChartReadyRef.current) {
        return undefined;
      }
      return action(chart);
    }, []);

    useEffect(() => {
      const container = chartRef.current;
      if (!container) {
        return;
      }

      const existingInstance = echarts.getInstanceByDom(container);
      if (existingInstance) {
        existingInstance.dispose();
      }

      const chart = echarts.init(container, themeName);
      chartInstanceRef.current = chart;
      isChartReadyRef.current = false;

      return () => {
        isChartReadyRef.current = false;
        chart.dispose();
        chartInstanceRef.current = null;
      };
    }, [runSafelyOnChart, themeName]);

    useEffect(() => {
      if (width <= 0 || height <= 0) {
        return;
      }
      const chart = chartInstanceRef.current;
      if (!chart || chart.isDisposed?.()) {
        return;
      }
      chart.resize();
    }, [width, height]);

    useEffect(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance) {
        return;
      }
      const isDisposed = chartInstance.isDisposed?.();
      if (isDisposed) {
        return;
      }

      isChartReadyRef.current = false;
      chartInstance.dispatchAction({ type: 'hideTip' });
      chartInstance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
      chartInstance.setOption(options, {
        notMerge: false,
        replaceMerge: ['series', 'graphic', 'xAxis', 'yAxis'],
      });
      isChartReadyRef.current = true;
      if (suppressTooltips) {
        chartInstance.dispatchAction({ type: 'hideTip' });
        chartInstance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
      }
    }, [options, suppressTooltips]);

    useEffect(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || !onSharedHoverChange || suppressTooltips) {
        return;
      }

      const clearLocalHover = () => {
        runSafelyOnChart((instance) => {
          instance.dispatchAction({ type: 'hideTip' });
          instance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
        });
      };

      const handleMouseMove = (params: HeatmapEventParams) => {
        if (
          params?.componentType === 'series' &&
          params?.seriesType === 'heatmap' &&
          typeof params.dataIndex === 'number'
        ) {
          onSharedHoverChange(params.dataIndex, chartId);
          return;
        }

        if (params?.componentType === 'grid') {
          clearLocalHover();
          onSharedHoverChange(null, chartId);
          return;
        }

        const offsetX = params?.event?.offsetX;
        const offsetY = params?.event?.offsetY;
        const pointerInsideGrid =
          typeof offsetX === 'number' &&
          typeof offsetY === 'number' &&
          !!runSafelyOnChart((instance) => instance.containPixel({ gridIndex: 0 }, [offsetX, offsetY]));

        if (pointerInsideGrid) {
          clearLocalHover();
        }

        onSharedHoverChange(null, chartId);
      };

      const handleMouseLeave = () => {
        clearLocalHover();
        onSharedHoverChange(null, chartId);
      };

      const handleSeriesMouseOut = (params: HeatmapEventParams) => {
        if (params?.componentType === 'series' && params?.seriesType === 'heatmap') {
          clearLocalHover();
          onSharedHoverChange(null, chartId);
        }
      };

      chartInstance.on('mousemove', handleMouseMove);
      chartInstance.on('mouseout', handleSeriesMouseOut);
      const zr = chartInstance.getZr();
      if (zr) {
        zr.on('globalout', handleMouseLeave);
      }

      return () => {
        chartInstance.off('mousemove', handleMouseMove);
        chartInstance.off('mouseout', handleSeriesMouseOut);
        if (zr) {
          zr.off('globalout', handleMouseLeave);
        }
      };
    }, [chartId, onSharedHoverChange, runSafelyOnChart, suppressTooltips, themeName]);

    useEffect(() => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || !sharedHover) {
        return;
      }
      if (suppressTooltips) {
        runSafelyOnChart((instance) => {
          instance.dispatchAction({ type: 'hideTip' });
          instance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
        });
        return;
      }
      if (sharedHover.dataIndex === null) {
        runSafelyOnChart((instance) => {
          instance.dispatchAction({ type: 'hideTip' });
          instance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
        });
        return;
      }

      if (sharedHover.sourceId === chartId) {
        return;
      }

      runSafelyOnChart((instance) => {
        instance.dispatchAction({ type: 'downplay', seriesIndex: 0 });
        instance.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: sharedHover.dataIndex,
        });
        instance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: sharedHover.dataIndex,
        });
      });
    }, [sharedHover, chartId, runSafelyOnChart, suppressTooltips]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%' }} />;
  },
);
